import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient } from "../lib/api";
import Seo from "../components/Seo";
import {
  Check, X as XIcon, Sparkles, ArrowRight, Loader2, Phone, Mail, Crown,
  Trophy, Star, ShieldCheck, BarChart3, Eye, Users, Target, Gem,
  Zap, Rocket, Award, Heart, Car, Image as ImageIcon, Headphones,
} from "lucide-react";
import { launchPrice } from "../lib/launchDiscount";
import { LaunchDiscountBadge } from "../components/LaunchDiscountBadge";

// ─────────────────────────────────────────────────────────────────────
// Plan catalog (mirrors DashboardPage TIERS)
// ─────────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: "basic",
    name_ar: "أساسي", name_en: "Basic",
    price: 0,
    accent: "#8a6f2e", accent_soft: "#c9a55c",
    bg_grad: "linear-gradient(135deg,#1a1a1a 0%,#0a0a0a 100%)",
    icon: Sparkles,
    tagline_ar: "ابدأ مجاناً", tagline_en: "Start for free",
    audience_ar: "للأفراد الذين يجرّبون البيع لأول مرة",
    audience_en: "Individuals testing the waters",
    cta_ar: "ابدأ مجاناً", cta_en: "Start free",
    available: true,
    cta_route: "/register",
  },
  {
    id: "plus",
    name_ar: "Plus للأفراد", name_en: "Individual Plus",
    price: 19,   // iter113 — matches backend catalog (payments_service.py:52)
    accent: "#0ea5e9", accent_soft: "#7dd3fc",
    bg_grad: "linear-gradient(135deg,#082f49 0%,#0c4a6e 50%,#075985 100%)",
    icon: Star,
    tagline_ar: "للأفراد الجادّين", tagline_en: "For serious sellers",
    audience_ar: "بائعون أفراد يريدون البروز والتحليلات",
    audience_en: "Sellers who want visibility & insights",
    cta_ar: "اشترك في Plus", cta_en: "Subscribe to Plus",
    available: true,
    best_value: true,
    best_value_label_ar: "الخيار الأفضل",
    best_value_label_en: "Best value",
  },
  {
    id: "dealer_pro",
    name_ar: "Pro للتجار", name_en: "Dealer Pro",
    price: 199,
    accent: "#c9a55c", accent_soft: "#e8c873",
    bg_grad: "linear-gradient(135deg,#1a1208 0%,#3d2e10 50%,#5a4615 100%)",
    icon: Trophy,
    tagline_ar: "للمعارض والتجار", tagline_en: "For dealers & showrooms",
    audience_ar: "تجار يديرون أساطيل ويستهدفون النمو السريع",
    audience_en: "Dealers managing inventory & growth",
    cta_ar: "اشترك في Pro", cta_en: "Subscribe to Pro",
    available: true,
    popular: true,
    badge_ar: "الأكثر طلباً", badge_en: "Most popular",
  },
  {
    id: "enterprise",
    name_ar: "Enterprise", name_en: "Enterprise",
    price: null,
    accent: "#10b981", accent_soft: "#34d399",
    bg_grad: "linear-gradient(135deg,#022c1a 0%,#0a3d2c 50%,#063e21 100%)",
    icon: Crown,
    tagline_ar: "للشركات والمؤسسات", tagline_en: "For companies & fleets",
    audience_ar: "شركات تأجير، أساطيل، ومؤسسات متعددة الفروع",
    audience_en: "Rental companies, fleets, multi-branch ops",
    cta_ar: "اطلب عرض سعر", cta_en: "Request a quote",
    available: false,
    badge_ar: "حسب الطلب", badge_en: "Custom",
  },
];

// ─────────────────────────────────────────────────────────────────────
// Feature comparison matrix (rows × plans)
// ─────────────────────────────────────────────────────────────────────
const FEATURES = [
  { group_ar: "الإعلانات والظهور", group_en: "Listings & visibility", icon: Car, rows: [
    { ar: "إعلانات نشطة", en: "Active listings", basic: "5", plus: "10", dealer_pro: "∞", enterprise: "∞" },
    { ar: "عدد الصور لكل إعلان", en: "Photos per listing", basic: "6", plus: "15", dealer_pro: "30", enterprise: "60" },
    { ar: "إعلانات مميّزة شهرياً", en: "Featured listings / month", basic: false, plus: "5", dealer_pro: "20", enterprise: "∞" },
    { ar: "أولوية في نتائج البحث", en: "Search priority", basic: false, plus: "متوسطة", dealer_pro: "عالية", enterprise: "قصوى" },
    { ar: "تمييز \"مميز\" دائم", en: "Permanent \"Featured\" pin", basic: false, plus: false, dealer_pro: true, enterprise: true },
  ]},
  { group_ar: "التحليلات والأداء", group_en: "Analytics & performance", icon: BarChart3, rows: [
    { ar: "إحصائيات أساسية", en: "Basic stats", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "تحليلات اتجاه 12 أسبوع", en: "12-week trend analytics", basic: false, plus: true, dealer_pro: true, enterprise: true },
    { ar: "نسب التحويل لكل إعلان", en: "Per-listing conversion rates", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "توصيات بالذكاء الاصطناعي", en: "AI recommendations", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "تقارير ROI أسبوعية", en: "Weekly ROI reports", basic: false, plus: false, dealer_pro: false, enterprise: true },
  ]},
  { group_ar: "أدوات البيع", group_en: "Sales tools", icon: Target, rows: [
    { ar: "صندوق رسائل موحّد", en: "Unified inbox", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "بحوث محفوظة + تنبيهات", en: "Saved searches + alerts", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "CRM لإدارة العملاء", en: "Built-in CRM", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "رفع جماعي بـ CSV", en: "Bulk CSV upload", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "صفحة معرض/بائع فاخرة بالعلامة التجارية", en: "Custom-branded showroom page", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "وصول API + Webhooks", en: "API + Webhooks access", basic: false, plus: false, dealer_pro: true, enterprise: true },
    { ar: "إدارة أسطول كاملة", en: "Full fleet management", basic: false, plus: false, dealer_pro: false, enterprise: true },
  ]},
  { group_ar: "الثقة والخدمات", group_en: "Trust & services", icon: ShieldCheck, rows: [
    { ar: "ضمان كارتال (اختياري — 150 ر.س ثابتة)", en: "CARTAL Escrow (optional — 150 SAR flat)", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "شارة التحقّق (KYC)", en: "Verified badge (KYC)", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "حلّ النزاعات", en: "Dispute resolution", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "شارة \"Pro\" مميزة", en: "Premium tier badge", basic: false, plus: "Plus", dealer_pro: "Pro للتجار", enterprise: "Enterprise" },
  ]},
  { group_ar: "الدعم", group_en: "Support", icon: Headphones, rows: [
    { ar: "دعم عبر البريد", en: "Email support", basic: true, plus: true, dealer_pro: true, enterprise: true },
    { ar: "دعم بأولوية", en: "Priority support", basic: false, plus: true, dealer_pro: true, enterprise: true },
    { ar: "خط ساخن مخصّص 24/7", en: "Dedicated 24/7 hotline", basic: false, plus: false, dealer_pro: false, enterprise: true },
    { ar: "مدير حساب شخصي", en: "Dedicated account manager", basic: false, plus: false, dealer_pro: false, enterprise: true },
    { ar: "اتفاقية مستوى خدمة (SLA)", en: "SLA agreement", basic: false, plus: false, dealer_pro: false, enterprise: true },
  ]},
];

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const currentTier = user?.subscription_tier || "basic";

  const handlePlanClick = async (p) => {
    if (p.id === "basic") {
      if (user) nav("/dashboard");
      else nav("/register");
      return;
    }
    if (p.id === "enterprise") {
      nav("/contact?subject=enterprise-quote");
      return;
    }
    if (!user) { nav("/login", { state: { from: "/pricing" } }); return; }
    // Route through the Moyasar hosted checkout — replaces legacy Stripe redirect.
    // Backend catalog expects `individual_plus` / `dealer_pro` (unchanged mapping).
    const apiSku = p.id === "plus" ? "individual_plus" : p.id;
    nav(`/checkout/moyasar?purpose=subscription&sku=${encodeURIComponent(apiSku)}`);
  };

  const displayPrice = (p) => {
    if (p.price === null) return null;
    if (p.price === 0) return 0;
    return billing === "annual" ? Math.round(p.price * 12 * 0.85) : p.price;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid="pricing-page">
      <Seo
        title={lang === "ar" ? "باقات واشتراكات كارتال | التسعير" : "CARTAL Plans & Pricing"}
        description={lang === "ar" ? "باقات كارتال للأفراد والمعارض — قارن المزايا والأسعار واختر الباقة الأنسب لنمو إعلاناتك على المنصة." : "CARTAL plans for individuals and showrooms — compare features, pricing and choose the right plan to grow your listings on the platform."}
        canonicalPath="/pricing"
      />
      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> {lang === "ar" ? "الباقات والاشتراكات" : "Plans & subscriptions"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] font-semibold leading-[1.05]">
          {lang === "ar"
            ? <>قارن الباقات.<br /><span className="text-[#8a6f2e]">اختر النمو الذي يناسبك.</span></>
            : <>Compare plans.<br /><span className="text-[#8a6f2e]">Pick the growth that fits.</span></>}
        </h1>
        <p className="text-[#3a3a3a] mt-4 leading-relaxed font-medium">
          {lang === "ar"
            ? "كل ما تحتاجه لبيع وشراء وتمويل المركبات — في باقة واحدة. ابدأ مجاناً، طوّر متى شئت."
            : "Everything you need to buy, sell & finance vehicles — in one plan. Start free, upgrade anytime."}
        </p>

        {/* Billing toggle */}
        <div className="mt-6 inline-flex items-center rounded-full p-1 surface gap-1" data-testid="billing-toggle">
          {[
            { id: "monthly", ar: "شهري", en: "Monthly" },
            { id: "annual", ar: "سنوي · وفّر 15%", en: "Annual · save 15%" },
          ].map((b) => (
            <button
              key={b.id}
              onClick={() => setBilling(b.id)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all`}
              style={billing === b.id
                ? { background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }
                : { background: "transparent", color: "#525252" }}
              data-testid={`billing-${b.id}`}
            >
              {lang === "ar" ? b.ar : b.en}
            </button>
          ))}
        </div>
      </div>

      {/* ── Plan cards ───────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="plan-grid">
        {PLANS.map((p) => {
          const Icon = p.icon;
          const isCurrent = currentTier === p.id;
          const price = displayPrice(p);
          return (
            <div
              key={p.id}
              className={`rounded-3xl p-6 relative ${p.popular || p.best_value ? "lg:scale-[1.04] lg:-mt-2" : ""} ${isCurrent ? "ring-4 ring-offset-2" : ""} ${p.best_value ? "best-value-glow" : ""}`}
              style={{
                background: p.bg_grad,
                border: `2px solid ${p.best_value ? "#e8c873" : p.accent}`,
                ...(isCurrent ? { boxShadow: `0 0 0 4px ${p.accent}40` } : {}),
              }}
              data-testid={`plan-card-${p.id}`}
            >
              {/* Floating golden "Best value" ribbon */}
              {p.best_value && (
                <div
                  className="absolute -top-4 start-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider shadow-xl whitespace-nowrap best-value-badge"
                  style={{
                    background: "linear-gradient(135deg,#f5d478 0%,#e8c873 25%,#c9a55c 50%,#e8c873 75%,#f5d478 100%)",
                    backgroundSize: "200% 100%",
                    color: "#0a0a0a",
                    border: "1.5px solid #a3822c",
                  }}
                  data-testid={`plan-best-value-${p.id}`}
                >
                  <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
                  {lang === "ar" ? p.best_value_label_ar : p.best_value_label_en}
                </div>
              )}

              {/* Background glow */}
              <div className="absolute -top-10 -end-10 w-32 h-32 rounded-full opacity-20 blur-2xl overflow-hidden" style={{ background: p.accent }} />

              {/* iter108 — launch discount ribbon ONLY on monthly.
                  For yearly we advertise the older 15% annual savings
                  instead (see the price block below). */}
              {p.price !== null && p.price > 0 && billing !== "annual" && launchPrice(p.price).active && (
                <div
                  className="absolute top-0 start-0 z-20 rounded-br-2xl rounded-tl-3xl px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-lg"
                  style={{
                    background: "linear-gradient(135deg,#ef4444 0%,#b91c1c 100%)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                  data-testid={`plan-launch-ribbon-${p.id}`}
                >
                  {lang === "ar" ? "خصم 50٪ · إطلاق" : "50% OFF · LAUNCH"}
                </div>
              )}
              {p.price !== null && p.price > 0 && billing === "annual" && (
                <div
                  className="absolute top-0 start-0 z-20 rounded-br-2xl rounded-tl-3xl px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-lg"
                  style={{
                    background: "linear-gradient(135deg,#0ea5e9 0%,#0369a1 100%)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                  data-testid={`plan-annual-ribbon-${p.id}`}
                >
                  {lang === "ar" ? "خصم سنوي 15٪" : "15% OFF · YEARLY"}
                </div>
              )}

              {(p.badge_en || isCurrent) && (
                <div className="absolute top-4 end-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider"
                  style={{ background: p.accent, color: p.id === "dealer_pro" ? "#0a0a0a" : "#fff" }}
                  data-testid={`plan-badge-${p.id}`}
                >
                  {isCurrent
                    ? (lang === "ar" ? "باقتك الحالية" : "Your plan")
                    : (lang === "ar" ? p.badge_ar : p.badge_en)}
                </div>
              )}

              <div className="relative">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${p.accent}30`, border: `1.5px solid ${p.accent}` }}>
                  <Icon className="w-6 h-6" style={{ color: p.accent_soft }} strokeWidth={2.2} />
                </div>

                <div className="overline mb-1" style={{ color: p.accent_soft }}>
                  {lang === "ar" ? p.tagline_ar : p.tagline_en}
                </div>
                <h3 className="font-display text-2xl font-bold" style={{ color: "#f7eecd" }}>
                  {lang === "ar" ? p.name_ar : p.name_en}
                </h3>
                <p className="text-xs mt-1.5 font-medium leading-snug" style={{ color: "rgba(247,238,205,0.7)" }}>
                  {lang === "ar" ? p.audience_ar : p.audience_en}
                </p>

                <div className="mt-5 flex items-baseline gap-1.5 min-h-[60px]">
                  {price !== null ? (
                    price === 0 ? (
                      <div className="font-display text-5xl font-bold" style={{ color: "#f7eecd" }}>
                        {lang === "ar" ? "مجاناً" : "Free"}
                      </div>
                    ) : (() => {
                      // iter108 — launch discount rules:
                      //   • Monthly billing → apply 50% launch discount (as before)
                      //   • Yearly billing  → skip the 50% launch and instead
                      //     surface the classic 15% annual savings so the buyer
                      //     always sees ONE clear discount at a time.
                      const isYearly = billing === "annual";
                      const info = isYearly ? null : launchPrice(price);
                      const yearlyFull = isYearly ? p.price * 12 : null;
                      const yearlySavings = isYearly ? (yearlyFull - price) : 0;
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <div className="font-display text-5xl font-black" style={{ color: "#f7eecd" }}>
                              {price.toLocaleString()}
                            </div>
                            <div className="text-xs font-medium" style={{ color: "rgba(247,238,205,0.7)" }}>
                              {lang === "ar"
                                ? (isYearly ? "ر.س / سنوياً" : "ر.س / شهرياً")
                                : (isYearly ? "SAR / year" : "SAR / month")}
                            </div>
                            {!isYearly && info && info.active && (
                              <span
                                className="font-display text-2xl line-through decoration-2"
                                style={{ color: "rgba(247,238,205,0.45)", textDecorationColor: "#ef4444" }}
                                data-testid={`plan-${p.id}-original-price`}
                              >
                                {info.original.toLocaleString()}
                              </span>
                            )}
                            {isYearly && (
                              <span
                                className="font-display text-2xl line-through decoration-2"
                                style={{ color: "rgba(247,238,205,0.45)", textDecorationColor: "#0ea5e9" }}
                                data-testid={`plan-${p.id}-yearly-original-price`}
                              >
                                {yearlyFull.toLocaleString()}
                              </span>
                            )}
                          </div>
                          {!isYearly && info && info.active && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5"
                                style={{ background: "rgba(239,68,68,0.20)", color: "#fecaca", border: "1px solid rgba(239,68,68,0.55)" }}
                                data-testid={`plan-${p.id}-savings-pill`}
                              >
                                {lang === "ar"
                                  ? `وفّرت ${info.savings.toLocaleString()} ر.س / شهرياً`
                                  : `Save ${info.savings.toLocaleString()} SAR / mo`}
                              </span>
                              <span className="text-[10px] font-bold" style={{ color: "#fca5a5" }}>
                                {lang === "ar" ? "عرض إطلاق محدود" : "Limited launch offer"}
                              </span>
                            </div>
                          )}
                          {/* iter113 — crystal-clear price transparency:
                              tell the buyer EXACTLY what they will be
                              charged, so there's no surprise between the
                              advertised price and the Moyasar checkout. */}
                          {!isYearly && info && info.active && (
                            <div
                              className="mt-1 rounded-lg px-2 py-1 text-[10px] font-bold text-center"
                              style={{ background: "rgba(16,185,129,0.15)", color: "#a7f3d0", border: "1px solid rgba(16,185,129,0.4)" }}
                              data-testid={`plan-${p.id}-actual-charge-note`}
                            >
                              {lang === "ar"
                                ? `سيُخصم منك ${info.current} ر.س فقط عند إتمام الدفع`
                                : `You'll be charged exactly ${info.current} SAR at checkout`}
                            </div>
                          )}
                          {isYearly && yearlySavings > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5"
                                style={{ background: "rgba(14,165,233,0.20)", color: "#bae6fd", border: "1px solid rgba(14,165,233,0.55)" }}
                                data-testid={`plan-${p.id}-yearly-savings-pill`}
                              >
                                {lang === "ar"
                                  ? `وفّرت ${yearlySavings.toLocaleString()} ر.س سنوياً`
                                  : `Save ${yearlySavings.toLocaleString()} SAR / year`}
                              </span>
                              <span className="text-[10px] font-bold" style={{ color: "#7dd3fc" }}>
                                {lang === "ar" ? "خصم 15٪ بالدفع السنوي" : "15% annual discount"}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="font-display text-2xl font-bold" style={{ color: "#f7eecd" }}>
                      {lang === "ar" ? "سعر مخصّص" : "Custom price"}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handlePlanClick(p)}
                  disabled={busy === p.id || isCurrent}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm shadow-xl transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={isCurrent
                    ? { background: "rgba(255,255,255,0.1)", color: "rgba(247,238,205,0.8)", border: `1.5px solid ${p.accent}` }
                    : {
                        background: `linear-gradient(135deg,${p.accent_soft} 0%,${p.accent} 60%,${p.accent} 100%)`,
                        color: p.id === "dealer_pro" ? "#0a0a0a" : "#0a0a0a",
                      }}
                  data-testid={`subscribe-${p.id}`}
                >
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? <ShieldCheck className="w-4 h-4" /> : <ArrowRight className="w-4 h-4 rtl:rotate-180" />}
                  {isCurrent
                    ? (lang === "ar" ? "باقتك النشطة" : "Your active plan")
                    : (lang === "ar" ? p.cta_ar : p.cta_en)}
                </button>

                {p.id === "enterprise" && !isCurrent && (
                  <button
                    type="button"
                    onClick={() => nav("/contact")}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold"
                    style={{ background: "rgba(255,255,255,0.06)", color: "rgba(247,238,205,0.8)", border: "1px solid rgba(255,255,255,0.15)" }}
                    data-testid="contact-us-enterprise"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {lang === "ar" ? "تواصل معنا" : "Talk to sales"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Detailed comparison table ─────────────────────── */}
      <div className="mt-20" data-testid="compare-table-wrap">
        <div className="text-center mb-10">
          <div className="overline overline-gold mb-2">
            {lang === "ar" ? "جدول المقارنة الكامل" : "Full comparison table"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-semibold">
            {lang === "ar" ? "افحص الفرق سطراً سطراً" : "See the difference, line by line"}
          </h2>
        </div>

        <div className="surface rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-table">
              {/* Header */}
              <thead>
                <tr style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 100%)" }}>
                  <th className="p-4 text-start font-semibold" style={{ color: "rgba(247,238,205,0.7)" }}>
                    {lang === "ar" ? "الميزة" : "Feature"}
                  </th>
                  {PLANS.map((p) => (
                    <th key={p.id} className="p-4 text-center min-w-[140px]">
                      <div className="font-display text-base font-bold" style={{ color: p.accent_soft }}>
                        {lang === "ar" ? p.name_ar : p.name_en}
                      </div>
                      <div className="text-[11px] font-medium mt-0.5" style={{ color: "rgba(247,238,205,0.6)" }}>
                        {p.price === null
                          ? (lang === "ar" ? "حسب الطلب" : "Custom")
                          : p.price === 0
                            ? (lang === "ar" ? "مجاناً" : "Free")
                            : `${p.price} ${lang === "ar" ? "ر.س / شهر" : "SAR/mo"}`}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {FEATURES.map((g) => (
                  <React.Fragment key={g.group_en}>
                    {/* Group heading row */}
                    <tr style={{ background: "#faf8f1" }}>
                      <td colSpan={5} className="p-3 px-5">
                        <div className="inline-flex items-center gap-2 font-bold text-[#8a6f2e] uppercase tracking-wider text-xs">
                          <g.icon className="w-4 h-4" />
                          {lang === "ar" ? g.group_ar : g.group_en}
                        </div>
                      </td>
                    </tr>
                    {g.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-[#e8e1c7]/50 hover:bg-[#fbf8ee] transition-colors">
                        <td className="p-3.5 px-5 text-[#0a0a0a] font-medium">
                          {lang === "ar" ? row.ar : row.en}
                        </td>
                        {PLANS.map((p) => (
                          <td key={p.id} className="p-3.5 text-center" data-testid={`cell-${p.id}-${ri}-${g.group_en.replace(/\s/g, "-")}`}>
                            <FeatureCell value={row[p.id]} accent={p.accent} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Bottom CTA strip ──────────────────────────────── */}
      <div className="mt-16 rounded-3xl p-8 sm:p-12 relative overflow-hidden text-center"
        style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1f1a10 50%,#3d2e10 100%)", border: "2px solid #c9a55c" }}
        data-testid="pricing-bottom-cta"
      >
        <div className="absolute -bottom-12 start-1/2 -translate-x-1/2 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "#c9a55c" }} />
        <div className="relative">
          <div className="overline mb-2" style={{ color: "#e8c873" }}>
            {lang === "ar" ? "غير متأكد أيهم تختار؟" : "Not sure which plan?"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold" style={{ color: "#f7eecd" }}>
            {lang === "ar" ? "ابدأ مجاناً — رقّ متى شئت" : "Start free — upgrade whenever"}
          </h2>
          <p className="text-sm mt-3 max-w-2xl mx-auto" style={{ color: "rgba(247,238,205,0.78)" }}>
            {lang === "ar"
              ? "كل الباقات قابلة للترقية أو الإلغاء فوراً. الرسوم بالريال السعودي عبر بوابة دفع Moyasar الآمنة."
              : "Every plan is upgradable or cancellable instantly. Pricing in SAR via secure Moyasar checkout."}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {!user && (
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm shadow-xl"
                style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}>
                {lang === "ar" ? "ابدأ مجاناً الآن" : "Start free now"} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </Link>
            )}
            <Link to="/faq" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm border border-[#c9a55c] text-[#e8c873] hover:bg-[#c9a55c]/10">
              {lang === "ar" ? "الأسئلة الشائعة" : "Read the FAQ"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Cell renderer — supports bool / string / numeric values
// ─────────────────────────────────────────────────────────────────────
function FeatureCell({ value, accent }) {
  if (value === true) return <Check className="w-5 h-5 mx-auto" style={{ color: accent }} strokeWidth={3} />;
  if (value === false || value === undefined) return <XIcon className="w-4 h-4 mx-auto text-[#cbcbcb]" />;
  // String / number values
  return (
    <span className="inline-block font-bold text-sm" style={{ color: accent }}>
      {value}
    </span>
  );
}
